import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { OpsType } from '../../types';
import { UploadConfig } from '../../api/services/coreService';
import { getOpsColor } from '../../utils/ops-color';

interface OpsConfig {
  name: string;
  displayName: string;
  dbName?: string;
  color: string;
  screens?: string[];
  uploads?: UploadConfig[];
}

interface OpsState {
  currentOps: OpsType | null;
  availableOps: OpsType[];
  opsConfigs: Record<string, OpsConfig>;
  fetchingConfigs: Record<string, boolean>;
}

// Default configurations for known operations (display info only)
const knownOps = [
  { name: 'rayo',        displayName: 'Rayo' },
  { name: 'rayosp',      displayName: 'Rayo SP' },
  { name: 'rayogyn',     displayName: 'Rayo GYN' },
  { name: 'rayobsb',     displayName: 'Rayo BSB' },
  { name: 'indrive',     displayName: 'InDrive' },
  { name: 'logus',       displayName: 'Logus' },
  { name: 'muve',        displayName: 'Muve' },
  { name: 'bee',         displayName: 'Bee' },
  { name: 'agility',     displayName: 'Agility' },
  { name: 'entrego',     displayName: 'Entrego' },
  { name: 'inovabc',     displayName: 'Inova BC' },
  { name: 'inovasp',     displayName: 'Inova SP' },
  { name: 'ppprime',     displayName: 'PP Prime' },
  { name: 'ppprimebru',  displayName: 'PP Prime BRU' },
  { name: 'rondolog',    displayName: 'Rondolog' },
  { name: 'xpto',        displayName: 'XPTO' },
  { name: 'gestor',      displayName: 'Gestor' },
];

const defaultOpsConfigs: Record<string, OpsConfig> = Object.fromEntries(
  knownOps.map(({ name, displayName }) => [
    name,
    { name, displayName, color: getOpsColor(name) },
  ])
);

const initialState: OpsState = {
  currentOps: null,
  availableOps: [],
  opsConfigs: defaultOpsConfigs,
  fetchingConfigs: {},
};

const opsSlice = createSlice({
  name: 'ops',
  initialState,
  reducers: {
    setCurrentOps: (state, action: PayloadAction<OpsType>) => {
      state.currentOps = action.payload;
    },
    setAvailableOps: (state, action: PayloadAction<OpsType[]>) => {
      state.availableOps = action.payload;
    },
    addOpsConfig: (
      state,
      action: PayloadAction<{ ops: string; config: OpsConfig }>
    ) => {
      state.opsConfigs[action.payload.ops] = action.payload.config;
    },
    updateOpsConfig: (
      state,
      action: PayloadAction<{ ops: string; config: Partial<OpsConfig> }>
    ) => {
      if (state.opsConfigs[action.payload.ops]) {
        state.opsConfigs[action.payload.ops] = {
          ...state.opsConfigs[action.payload.ops],
          ...action.payload.config,
        };
      }
    },
    clearOps: (state) => {
      state.currentOps = null;
      state.availableOps = [];
    },
    setFetchingConfig: (
      state,
      action: PayloadAction<{ ops: string; fetching: boolean }>
    ) => {
      state.fetchingConfigs[action.payload.ops] = action.payload.fetching;
    },
  },
});

export const {
  setCurrentOps,
  setAvailableOps,
  addOpsConfig,
  updateOpsConfig,
  clearOps,
  setFetchingConfig,
} = opsSlice.actions;
export default opsSlice.reducer;
